import {GameObjectType, TLevelObject} from "../../config/types";
import * as FF from "../core/FloatUtils.js";
import {finalize} from "../core/FloatUtils.js";
import {Vec2} from "../core/Vec2.js";
import {
  SimulationEvent,
  SimulationEventType,
} from "../manager/SimulationManager.js";
import {Player} from "../player/Player.js";
import GameObject from "./GameObject.js";

/**
 * Defines the detailed information of a contact point during a collision.
 */
export interface ContactPoint {
  position: Vec2; // Contact position in world space
  normal: Vec2; // Contact normal in world space
  penetration: number; // Penetration depth
  velocityAlongNormal: number; // Player's velocity projected onto the normal
}

/**
 * Defines the physical properties of a line's material.
 */
export type Material = {
  restitution: number; // Bounciness
  friction: number; // Slipperiness
};

/**
 * A utility function to look up material properties based on the line type.
 */
const lookupMaterial = (type: GameObjectType): Material => {
  switch (type) {
    case GameObjectType.PLASTIC:
      return {restitution: 1, friction: 0};
    case GameObjectType.ICE:
      return {restitution: 0.2, friction: 0};
    case GameObjectType.SAND:
      return {restitution: 0.1, friction: 0.45};
    case GameObjectType.FIRE:
      // Fire has no physical properties as it's a hazard.
      return {restitution: 0, friction: 0};
    default:
      throw new Error(`Unknown line type: ${type}`);
  }
};

/**
 * Represents a physical line segment (a capsule shape) in the game world.
 */
class Line extends GameObject {
  public readonly position: Vec2;
  public readonly rotation: number;
  public readonly scale: Vec2;
  public readonly material: Material;
  public readonly type: GameObjectType;
  public readonly radius: number;
  public readonly length: number;
  public readonly halfLength: number;
  private readonly internalRotation: number;

  constructor(
    position: Vec2,
    rotation: number,
    scale: Vec2,
    material: Material,
    type: GameObjectType,
  ) {
    super();
    this.position = position;
    this.rotation = rotation;
    this.scale = scale;
    this.material = material;
    this.type = type;

    // Radius is half the smaller dimension (width of the capsule)
    this.radius = finalize(Math.min(this.scale.x, this.scale.y) * FF.HALF);
    if (type === GameObjectType.FIRE) {
      this.radius = finalize(Math.max(0, this.radius - 0.5));
    }

    // Length is the extension of the capsule
    this.length = finalize(Math.abs(this.scale.x - this.scale.y));
    this.halfLength = finalize(this.length * FF.HALF);

    this.internalRotation =
      this.scale.x < this.scale.y
        ? finalize(this.rotation + FF.HALF_PI)
        : this.rotation;
  }

  static create(obj: TLevelObject): Line {
    const material = lookupMaterial(obj.type);
    return new Line(
      new Vec2(obj.position[0], obj.position[1]),
      obj.rotation,
      new Vec2(obj.scale[0], obj.scale[1]),
      material,
      obj.type,
    );
  }

  /**
   * Processes interactions with the player and returns an array of simulation events.
   * This method no longer modifies the Player object directly.
   */
  public process(player: Player): SimulationEvent[] {
    const localPos = this.transformPositionToLineSpace(player.currentPosition);
    const localVel = this.rotateVector(player.velocity);

    const collision = this.checkCollision(localPos, player);
    if (!collision.collided) {
      return []; // No collision, no events.
    }

    // Rule: FIRE type instantly generates a "death" event.
    if (this.type === GameObjectType.FIRE) {
      return [
        {
          type: SimulationEventType.PlayerHitHazard,
          player,
          hazardType: "FIRE_LINE",
        },
      ];
    }

    // For other line types, it's a physical collision.
    const contact = this.calculateContactPoint(
      localPos,
      localVel,
      player,
      collision,
    );

    // Only generate an event if the player is moving towards the line.
    if (contact.velocityAlongNormal < 0) {
      return [
        {
          type: SimulationEventType.PlayerHitLine,
          player,
          contact: contact,
          material: this.material,
        },
      ];
    }

    return [];
  }

  private checkCollision(
    localPos: Vec2,
    player: Player,
  ): {collided: boolean; contactX: number; contactY: number} {
    const closestX = Math.max(0, Math.min(localPos.x, this.length));
    const closestY = 0;
    const delta = localPos.clone().sub(new Vec2(closestX, closestY));
    const distanceSquared = delta.dot(delta);
    const radiusSum = player.radius + this.radius;
    return {
      collided: distanceSquared < radiusSum * radiusSum,
      contactX: closestX,
      contactY: closestY,
    };
  }

  private calculateContactPoint(
    localPos: Vec2,
    localVel: Vec2,
    player: Player,
    collision: {
      contactX: number;
      contactY: number;
    },
  ): ContactPoint {
    const contactBase = new Vec2(collision.contactX, collision.contactY);
    const delta = localPos.clone().sub(contactBase);
    const distance = delta.length();
    const normal =
      distance === 0 ? new Vec2(0, 1) : delta.clone().div(distance);
    const penetration = player.radius + this.radius - distance;
    const velocityAlongNormal = localVel.dot(normal);
    const contactPointWorld = this.inverseRotateVector(
      contactBase.clone().sub(new Vec2(this.length / 2, 0)),
    ).add(this.position);
    const normalWorld = this.inverseRotateVector(normal);

    return {
      position: contactPointWorld,
      normal: normalWorld,
      penetration,
      velocityAlongNormal,
    };
  }

  private transformPositionToLineSpace(point: Vec2): Vec2 {
    const translated = point.clone().sub(this.position);
    const rotated = translated.clone().rotate(-this.internalRotation);
    rotated.x += this.halfLength;
    return rotated.finalize();
  }

  private rotateVector(velocity: Vec2): Vec2 {
    return velocity.clone().rotate(-this.internalRotation).finalize();
  }

  private inverseRotateVector(velocity: Vec2): Vec2 {
    return velocity.clone().rotate(this.internalRotation).finalize();
  }
}

export default Line;
